/**
 * PipelineStore — Single source of truth for pipeline data.
 * Loads via ReferenceDataStore (ETag + shared cache), keeps local Map API for consumers.
 */

import { ReferenceDataStore } from '../reference/ReferenceDataStore.js';
import '../reference/SyncManager.js';

const _store = {
    pipelines: new Map(),
    order: [],
    loading: false,
    subscribers: new Set(),
    initialized: false,
    loadPromise: null,
};

function _notify() {
    for (const fn of _store.subscribers) fn(_store.pipelines, _store.order);
}

function _sortByDisplayOrder() {
    _store.order.sort((a, b) => {
        const pa = _store.pipelines.get(a);
        const pb = _store.pipelines.get(b);
        const oa = pa?.display_order ?? 9999;
        const ob = pb?.display_order ?? 9999;
        return oa - ob || (pa?.name?.localeCompare(pb?.name) ?? 0);
    });
}

function _hydrateFromList(list) {
    _store.pipelines.clear();
    _store.order = [];
    if (!Array.isArray(list)) return;
    for (const p of list) {
        if (p.id && p.name) {
            _store.pipelines.set(p.id, {
                id: p.id,
                name: p.name,
                display_order: p.display_order,
                is_active: p.is_active,
                description: p.description,
            });
            _store.order.push(p.id);
        }
    }
    _sortByDisplayOrder();
}

/**
 * Normalize a pipeline key/value to a consistent internal form.
 * Falls back to 'digital_product' when missing/unknown.
 */
export function normalizePipelineKey(value) {
    if (!value) return 'digital_product';
    const v = String(value).trim().toLowerCase().replace(/[\s-]+/g, '_');
    const known = ['digital_product', 'enterprise', 'subscription', 'services', 'partnership'];
    return known.includes(v) ? v : 'digital_product';
}

export const PipelineStore = {
    /**
     * Initialize the store by fetching all pipelines from the API.
     * Idempotent — subsequent calls return the same promise / shared reference cache.
     */
    async initialize() {
        if (_store.initialized) return;
        if (_store.loadPromise) return _store.loadPromise;

        _store.loading = true;
        _store.loadPromise = (async () => {
            try {
                const list = await ReferenceDataStore.ensurePipelines();
                _hydrateFromList(list);
                _store.initialized = true;
            } catch (err) {
                console.warn('PipelineStore: failed to load pipelines', err);
                _store.initialized = true;
            } finally {
                _store.loading = false;
                _store.loadPromise = null;
                _notify();
            }
        })();
        return _store.loadPromise;
    },

    /** Subscribe to store changes. Returns unsubscribe function. */
    subscribe(fn) {
        _store.subscribers.add(fn);
        return () => _store.subscribers.delete(fn);
    },

    /** Get all pipelines as an array (sorted by display_order). */
    getAll() {
        return _store.order.map(id => _store.pipelines.get(id)).filter(Boolean);
    },

    /** Get a single pipeline by ID. */
    get(id) {
        return _store.pipelines.get(id);
    },

    /** Get display label for a pipeline ID. Falls back to ID if not found. */
    getLabel(id) {
        const p = _store.pipelines.get(id);
        return p?.name ?? id ?? '—';
    },

    /** Optimistically add a pipeline from a create response. */
    add(pipeline) {
        if (!pipeline?.id || !pipeline?.name) return;
        _store.pipelines.set(pipeline.id, {
            id: pipeline.id,
            name: pipeline.name,
            display_order: pipeline.display_order ?? 9999,
            is_active: pipeline.is_active,
            description: pipeline.description
        });
        if (!_store.order.includes(pipeline.id)) _store.order.push(pipeline.id);
        _sortByDisplayOrder();
        _notify();
    },

    /** Optimistically update a pipeline. */
    update(pipeline) {
        const existing = _store.pipelines.get(pipeline.id);
        if (!existing) return;
        _store.pipelines.set(pipeline.id, { ...existing, ...pipeline });
        _sortByDisplayOrder();
        _notify();
    },

    /** Optimistically remove a pipeline. */
    remove(id) {
        _store.pipelines.delete(id);
        _store.order = _store.order.filter(x => x !== id);
        _notify();
    },

    /** Force a full re-fetch from the API. */
    async refresh() {
        _store.initialized = false;
        _store.loadPromise = null;
        const list = await ReferenceDataStore.ensurePipelines({ force: true });
        _hydrateFromList(list);
        _store.initialized = true;
        _notify();
    }
};
